// Journal PDF export: puts the note's pages together by hand, so every page is
// filled with the note's own background and the text sits inside a margin on
// each one (Jade, 2026-10-18). html2pdf then just turns each finished page into
// a PDF page (it slices the tall canvas at exactly one page height).

// Where to end a page that starts at `start` and may hold up to `room` rows:
// the lowest empty row in the bottom 40% of the page, so a page never ends
// through the middle of a line of text. With no empty row there (one tall
// photo, say), it cuts at the full height.
export function pageCuts(rowEmpty, room) {
  const total = rowEmpty.length;
  const cuts = [];
  let start = 0;
  while (start < total) {
    let end = start + room;
    if (end >= total) { cuts.push([start, total]); break; }
    const floor = start + Math.floor(room * 0.6);
    for (let y = end; y > floor; y--) {
      if (rowEmpty[y]) { end = y; break; }
    }
    cuts.push([start, end]);
    start = end;
    // The next page starts at the next line, not with the gap left above it.
    while (start < total && rowEmpty[start]) start++;
  }
  return cuts.length ? cuts : [[0, 0]];
}

// Which rows of the text canvas are fully transparent. A canvas tainted by a
// cross-origin image can't be read: then every row counts as text and pages
// are simply cut at their full height.
function emptyRows(canvas) {
  const rows = new Array(canvas.height).fill(false);
  if (!canvas.width || !canvas.height) return rows;
  try {
    const data = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height).data;
    const stride = canvas.width * 4;
    for (let y = 0; y < canvas.height; y++) {
      let empty = true;
      for (let i = y * stride + 3, end = i + stride; i < end; i += 4) {
        if (data[i] > 8) { empty = false; break; }
      }
      rows[y] = empty;
    }
  } catch (e) { /* tainted canvas */ }
  return rows;
}

// text: the note rendered on a transparent canvas, as wide as the page minus
// both margins. bg: one page of the note's background. margin: in canvas px.
// Returns one tall canvas made of whole A4 pages stacked top to bottom.
export function composeNotePages(text, bg, margin) {
  const width = bg.width;
  // Same page height html2pdf slices at for a margin-free A4 page.
  const pageH = Math.floor(width * 297 / 210);
  const room = Math.max(1, pageH - 2 * margin);
  const cuts = pageCuts(emptyRows(text), room);
  const out = document.createElement("canvas");
  out.width = width;
  out.height = pageH * cuts.length;
  const ctx = out.getContext("2d");
  cuts.forEach(([from, to], i) => {
    const top = i * pageH;
    ctx.drawImage(bg, 0, 0, bg.width, bg.height, 0, top, width, pageH);
    if (to > from) ctx.drawImage(text, 0, from, text.width, to - from, margin, top + margin, text.width, to - from);
  });
  return out;
}
